/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { string, shape, func, array, bool } from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, NavLink } from 'react-router-dom';

import p from 'polyglot/polyglot';

import {
  getPosts as getPostsAction,
  getCategories as getCategoriesAction,
} from 'actions/blog';

import { createUrl } from 'utils/urlHelper';

import ArticleCard from 'components/ArticleCard/ArticleCard';
import SpinnerIcon from 'components/SpinnerIcon/SpinnerIcon';
import MapWPCategories from 'components/MapWPCategories/MapWPCategories';

import styles from './blog.scss';

/**
 * This is the Blog component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class Blog extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    match: shape({
      params: shape({
        category: string,
      }).isRequired,
    }).isRequired,
    blog: shape({
      loading: bool.isRequired,
      items: array.isRequired,
    }).isRequired,
    blogCategories: shape({
      loading: bool.isRequired,
      categories: array.isRequired,
    }).isRequired,
    getPosts: func.isRequired,
    getCategories: func.isRequired,
  };

  componentDidMount() {
    const {
      blogCategories: { categories },
      getPosts,
      getCategories,
    } = this.props;

    getPosts();

    // if we don't have blog categories fetch them.
    if (categories.length <= 0) {
      getCategories();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      match: { params: prevParams },
    } = prevProps;
    const {
      getPosts,
      match: { params },
    } = this.props;

    // if category has changed.
    if (prevParams.category !== params.category) {
      if (params.category) {
        getPosts(`?category=${params.category}`);
      } else {
        getPosts();
      }
    }
  }

  renderCategories = () => {
    const {
      blogCategories: { loading, categories },
    } = this.props;

    if (loading) return null;

    return (
      <ul className={styles.categories}>
        <li className={styles.category}>
          <NavLink exact activeClassName={styles.active} to={createUrl('news')}>
            {p.t('blog.categories.all_posts')}
          </NavLink>
        </li>
        <MapWPCategories
          className={styles.category}
          activeClassName={styles.active}
          categories={categories}
        />
      </ul>
    );
  };

  /**
   * Render Blog.
   *
   * @return {Object}
   */
  render() {
    const {
      blog: { loading, items },
    } = this.props;

    return (
      <section className={styles.wrapper}>
        <header className={styles.header}>
          <div className={styles.infoWrapper}>
            <div className={styles.row}>
              <div className={styles.text}>
                <h1 className={styles.title}>{p.t('blog.title')}</h1>
                <p className={styles.info}>{p.t('blog.tagline')}</p>
                {this.renderCategories()}
              </div>
            </div>
          </div>
        </header>

        <div className={styles.content}>
          <div className={styles.posts}>
            {loading && (
              <div className={styles.spinner}>
                <SpinnerIcon small />
              </div>
            )}

            {items.map(({ slug, ...item }) => {
              const img = item?.post_thumbnail?.URL
                ? item?.post_thumbnail?.URL
                : '#';
              return (
                <div className={styles.post} key={slug}>
                  <ArticleCard
                    {...item}
                    content={null}
                    url={createUrl('blogPost', slug)}
                    image={img}
                    hasSquareImage
                    hasButton
                  />
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }
}

export default withRouter(
  connect(
    ({ auth, blog, blogCategories }) => ({ auth, blog, blogCategories }),
    {
      getPosts: getPostsAction,
      getCategories: getCategoriesAction,
    }
  )(Blog)
);
